import { endOfYear } from "date-fns";

import { type AnybillResult, AnybillResultHelper } from "~/additionalModels/AnybillResponse";
import ApiConfig from "~/config/ApiConfig";
import { AnybillHeaders } from "~/models/interfaces/AnybillHeaders";

/** The session a `BaseService` sends its requests for. Bound once per Nuxt app by `plugins/api.ts`. */
export interface ApiSession {
  accessToken: () => string | undefined;
  logout: () => void;
}

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
type ApiResponseType = "json" | "blob" | "text" | "arrayBuffer" | "stream";

interface FetchRequest {
  method: HttpMethod;
  body?: any;
  headers: { [key: string]: string };
  query?: any;
  responseType: ApiResponseType;
}

let clientInstance: BaseService | undefined;

export default class BaseService {
  /**
   * The service of the current request. On the server every request has its own instance bound to
   * its own auth store; it is looked up through the request's Nuxt context, which
   * `experimental.asyncContext` keeps across `await`. The browser only ever has one Nuxt app.
   */
  static get Instance(): BaseService {
    if (import.meta.client && clientInstance)
      return clientInstance;

    const instance = tryUseNuxtApp()?.$api;
    if (!instance)
      throw new Error("[BaseService] Used outside of a Nuxt app context. Call it from a component, a plugin, a middleware or a store action.");
    return instance;
  }

  static setClientInstance(instance: BaseService): void {
    clientInstance = instance;
  }

  private readonly session: ApiSession;

  constructor(session: ApiSession) {
    this.session = session;
  }

  /**
   * ASP.NET answers model validation failures with a `ValidationProblemDetails`, whose `errors` is
   * a `{ field: [message, ...] }` dictionary — not the error array the anybill API uses elsewhere.
   * `title` is only the generic "One or more validation errors occurred." headline, so the actual
   * reasons live in that dictionary. Returns undefined for every other shape.
   */
  private static describeValidationProblem(data: any): string | undefined {
    const errors = data?.errors;
    if (!errors || Array.isArray(errors) || typeof errors !== "object")
      return undefined;

    const messages = Object.values(errors)
      .flat()
      .filter((message): message is string => typeof message === "string");

    return messages.length > 0 ? messages.join("\n") : undefined;
  }

  private static handleError<T>(
    errors: any,
    statusCode: number,
    errorString?: string,
  ): AnybillResult<T> {
    // TODO check what error is coming in and react to it
    // Only the anybill error array is indexable. A ValidationProblemDetails dictionary lands here
    // too, and `errors[0]` on that is undefined — which used to swallow the message entirely.
    if (!Array.isArray(errors) || errors.length === 0) {
      if (errorString)
        return AnybillResultHelper.failure(errorString, statusCode, null);

      return AnybillResultHelper.failure(null, statusCode, null);
    }
    else {
      return AnybillResultHelper.failure(errors[0], statusCode, errors[0].code);
    }
  }

  async get<T>({
    path,
    body,
    responseType,
    headers,
    query,
    authNeeded,
    isComponentFetch = false,
    isDataAnalytics = false,
  }: {
    path: string;
    body?: any;
    responseType?: "json" | "blob" | "text" | "arrayBuffer" | "stream";
    headers?: any;
    query?: any;
    authNeeded?: boolean;
    isComponentFetch?: boolean;
    isDataAnalytics?: boolean;
  }): Promise<AnybillResult<T>> {
    return this.call(
      "GET",
      path,
      body,
      responseType,
      headers,
      query,
      authNeeded,
      undefined,
      isDataAnalytics,
      { isComponentFetch },
    );
  }

  async post<T>({
    path,
    body,
    responseType,
    headers,
    query,
    authNeeded,
    isDataAnalytics,
    isComponentFetch = false,
  }: {
    path: string;
    body?: any;
    responseType?: "json" | "blob" | "text" | "arrayBuffer" | "stream";
    headers?: any;
    query?: any;
    authNeeded?: boolean;
    isDataAnalytics?: boolean;
    isComponentFetch?: boolean;
  }): Promise<AnybillResult<T>> {
    return this.call(
      "POST",
      path,
      body,
      responseType,
      headers,
      query,
      authNeeded,
      undefined,
      isDataAnalytics,
      { isComponentFetch },
    );
  }

  async put<T>({
    path,
    body,
    responseType,
    headers,
    query,
    authNeeded,
    isComponentFetch = false,
  }: {
    path: string;
    body?: any;
    responseType?: "json" | "blob" | "text" | "arrayBuffer" | "stream";
    headers?: any;
    query?: any;
    authNeeded?: boolean;
    isComponentFetch?: boolean;
  }): Promise<AnybillResult<T>> {
    return this.call(
      "PUT",
      path,
      body,
      responseType,
      headers,
      query,
      authNeeded,
      undefined,
      false,
      { isComponentFetch },
    );
  }

  async delete<T>({
    path,
    body,
    responseType,
    headers,
    query,
    authNeeded,
  }: {
    path: string;
    body?: any;
    responseType?: "json" | "blob" | "text" | "arrayBuffer" | "stream";
    headers?: any;
    query?: any;
    authNeeded?: boolean;
  }): Promise<AnybillResult<T>> {
    return this.call("DELETE", path, body, responseType, headers, query, authNeeded);
  }

  async patch<T>({
    path,
    body,
    responseType,
    headers,
    query,
    authNeeded,
  }: {
    path: string;
    body?: any;
    responseType?: "json" | "blob" | "text" | "arrayBuffer" | "stream";
    headers?: any;
    query?: any;
    authNeeded?: boolean;
  }): Promise<AnybillResult<T>> {
    return this.call("PATCH", path, body, responseType, headers, query, authNeeded);
  }

  private async call<T>(
    method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
    path: string,
    body?: any,
    responseType: "json" | "blob" | "text" | "arrayBuffer" | "stream" = "json",
    headers?: { [key: string]: string },
    query?: any,
    authNeeded: boolean = true,
    tokenRefreshed: boolean = false, // TODO: refresh removed temporarily
    isDataAnalytics: boolean = false,
    _params?: {
      isComponentFetch: boolean;
    },
  ): Promise<AnybillResult<T>> {
    const header = headers ?? {};

    // TODO: very ugly, do with proxy
    const url = isDataAnalytics
      ? ApiConfig.dataAnalyticsUrl + path
      : ApiConfig.baseUrl + path;
    if (authNeeded && !this.authorize(header))
      return AnybillResultHelper.failure("Unauthorized", 401, null);

    // During SSR data is fetched twice, once on the server and once on the client.
    if (_params?.isComponentFetch)
      return BaseService.componentFetch<T>(url, { method, body, headers: header, query, responseType });

    // use $fetch for component calls and useFetch only for setup
    return this.setupFetch<T>(url, { method, body, headers: header, query, responseType });
  }

  /** Adds the session's bearer token to `header`. Without a token the session is logged out. */
  private authorize(header: { [key: string]: string }): boolean {
    const accessToken = this.session.accessToken();
    if (!accessToken) {
      this.session.logout();
      return false;
    }
    header.Authorization = `Bearer ${accessToken}`;
    return true;
  }

  private static async componentFetch<T>(url: string, fetchOptions: FetchRequest): Promise<AnybillResult<T>> {
    const responseHeaders = ref<AnybillHeaders | undefined>();
    let responseError: Error | undefined;

    try {
      const response = await $fetch<string>(url, {
        ...fetchOptions,
        timeout: 45000,
        onResponse({ error, response }) {
          responseError = error;
          responseHeaders.value = response.headers;
        },
      });

      if (responseError) {
        // currently no refresh
        return BaseService.handleError([responseError], 400);
      }
      return AnybillResultHelper.success(
        ModelKeyConverter.keyToLowerCamel(response) as T,
        200,
        responseHeaders.value,
      );
    }
    catch (e) {
      return BaseService.handleError([(e as any).data ?? "-", (e as any).cause], 400);
    }
  }

  private async setupFetch<T>(url: string, fetchOptions: FetchRequest): Promise<AnybillResult<T>> {
    const responseHeaders = ref<AnybillHeaders | undefined>();
    // TODO: https://www.npmjs.com/package/nuxt-cookies-auth possible solution!
    const { data, error } = await useFetch(url, {
      ...fetchOptions,
      timeout: 30000,
      onResponse({ request, response, options }) {
        // Create an instance of AnybillHeaders from the response headers
        responseHeaders.value = new AnybillHeaders(response.headers);
      },
    });

    if (error.value)
      return this.handleFetchError<T>(fetchOptions.method, error.value);

    if (!data.value)
      return AnybillResultHelper.success(undefined as T, 204, responseHeaders.value);

    // Response keys are converted to lower camel case. Files/arraybuffers from the backend are returned as they are.
    const returnData = fetchOptions.responseType === "arrayBuffer"
      ? data.value as T
      : ModelKeyConverter.keyToLowerCamel(data.value) as T;
    return AnybillResultHelper.success(returnData, 200, responseHeaders.value);
  }

  private async handleFetchError<T>(method: HttpMethod, error: any): Promise<AnybillResult<T>> {
    if (error.statusCode === 401) {
      this.session.logout();

      await AnybillLogger.instance
        .warn(
          `[${method}] Unauthorized during fetch request\n${error.data?.title ?? "- No title -"}\n${error.cause ?? "- No cause -"}`,
          error,
          "BaseService",
        );
      return BaseService.handleError(error.data?.errors ?? [], error.statusCode ?? 0);
    }
    if (error.data?.title) {
      await AnybillLogger.instance
        .error(
          `[${method}] Exception caught during fetch request\n${error.data?.title}\n${error.cause}`,
          error,
          "BaseService",
        );
      const errorString = BaseService.describeValidationProblem(error.data)
        ?? `${error.data?.title}\n${error.cause}`;
      return BaseService.handleError(error.data?.errors ?? [], error.statusCode ?? 0, errorString);
    }

    await AnybillLogger.instance
      .error(
        `[${method}] Exception caught during fetch request`,
        error,
        "BaseService",
      );

    const plainMessage = typeof error.data === "string" ? error.data : undefined;
    return BaseService.handleError(error.data?.errors ?? [], error.statusCode ?? 0, plainMessage);
  }
}
